import { observer } from "mobx-react";
import React from "react";

import { loadJs } from "@use/load-js";

import { EmbedBlockModel } from "@courses-models/course";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { config } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/config";
import { urls } from "@apps/viewer/src/ts/config";

import {
  Preview,
  PreviewProps,
  PreviewState,
  PreviewTitle,
} from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/Preview";
import { gGateway } from "@utils/gateway";

/**
 * Renders a preview of embed blocks consisting of title and embed domain.
 */
@observer
class EmbedPreviewUnrapped extends Preview<PreviewProps<EmbedBlockModel>> {
  /**
   * Set up initial state.
   */
  public readonly state: PreviewState = {
    isActive: false,
  };

  /**
   * Renderer that'll inject the fallback icon, which is going to be used if
   * no thumbnail could be found for this block.
   */
  protected renderThumbnailFallbackIcon(): React.JSX.Element {
    return (
      <i>
        {getIcon("embed", { fillColor: config.thumbnailIconColor })}

        <style jsx>{`
          /** Position the icon */
          i {
            /** Position at the bottom-left corner */
            position: absolute;
            top: 60px;
            right: -30px;

            /** Set up sizing */
            width: 250px;
            height: 110px;
          }
        `}</style>
      </i>
    );
  }

  /**
   * There are no SubTitles available for YouTube previews, simply return
   * title.
   */
  protected getTitle(): PreviewTitle {
    return {
      title: this.props.model.title,
    };
  }

  /**
   * For embedded items, we can safely use the src of the iframe as the key,
   * which our preview depends upon.
   */
  protected getDataKey(): string | undefined {
    return this.props.model.src;
  }

  /**
   * MOCK - We need to implement a fetchContent, but there's really no other data
   * needed in order to render the preview for this block...
   */
  protected async fetchContent(): Promise<void> {
    return;
  }

  /**
   * Helper that'll return a url to the thumbnail of a given Youtube video.
   */
  protected async getThumbnailUrl(
    src: string | undefined,
  ): Promise<string | undefined> {
    // Bail out if no video has been identified
    if (src === undefined) {
      return;
    }

    // Wait for the gExport service script to be loaded
    await loadJs(urls.gExport);

    // Bail out if gExport is unavailable
    // tslint:disable-next-line no-typeof-undefined
    if (typeof window.gExport === "undefined") {
      return;
    }

    window.gExport.utils.registerRequestInterceptor(
      (input: RequestInfo | URL, init?: RequestInit) => {
        if (input instanceof Request) {
          return new Request(gGateway(input.url), input);
        } else if (input instanceof URL) {
          return new Request(gGateway(input.toString()), init);
        } else {
          return new Request(gGateway(input), init);
        }
      },
    );

    // Calculate desired aspect ratio
    const { desiredHeight, desiredWidth } = this.props.model;

    const aspectRatio =
      desiredHeight && desiredWidth ? desiredHeight / desiredWidth : 0.5625;

    // Wait for a screendump to be generated
    // tslint:disable-next-line await-promise
    const screenshot = await window.gExport.convert({
      aspectRatio,
      format: "jpg",
      input: src,

      // Cache screengrab for up to 1 week
      cache: 7 * 24 * 60 * 60 * 1000,

      // Generate screenshot using iframe embed-mode (as that's exactly
      // how the url will be embedded in this client, it will provide a
      // more realistic output)
      embed: true,
    });

    // ... Otherwise resolve with the url to the thumbnail for this video
    return screenshot.url;
  }
}

export const EmbedPreview = EmbedPreviewUnrapped;
